import { Box, css, Flex } from 'theme-ui';
import RawHtml from '../../components/RawHtml';
import {
  BASIC_CONTAINER_WIDTH,
  CONTENT_TYPES_LEFT_MARGIN,
} from '../../consts/style-consts';
import styled from '../../lib/transient-styled';
import pxToRem from '../../utils/pixelsToRem';
import { LightRawHtml } from '../typography/HTML';
import headerPack from '../typography/headers';

export const Navigation = styled(Flex)(
  css({
    flexDirection: ['column', 'column', 'row'],
    gap: ['30px', '30px', '70px'],
  }),
  ({ $isDetailedNavActive, $opacity, $isVisibleOnPrint }) =>
    css({
      mb: $isDetailedNavActive
        ? ['37px', '37px', '78px']
        : ['46px', '46px', '72px'],
      opacity: $opacity,
      '@media print': $isVisibleOnPrint
        ? {
            opacity: 1,
          }
        : {},
    }),
);

export const DetailedPageNav = styled(Flex)(
  css({
    flexDirection: 'column',
    textAlign: 'center',
    width: BASIC_CONTAINER_WIDTH,
    mt: '20px',
  }),
);

export const OriginCountry = styled.p(
  css({
    mt: '18px',
    fontStyle: 'italic',
  }),
);

export const HeroBlock = styled(Flex)(
  css({
    flexDirection: ['column-reverse', 'column-reverse', 'row'],
    alignItems: ['center', 'center', 'normal'],
    gap: ['40px', '40px', '32px'],
    justifyContent: ['normal', 'normal', 'space-between'],
  }),
  ({ $isGallery }) =>
    css({
      width: $isGallery
        ? BASIC_CONTAINER_WIDTH
        : ['100%', '100%', `calc(64% + ${CONTENT_TYPES_LEFT_MARGIN})`],
      ml: $isGallery ? [0, 0, CONTENT_TYPES_LEFT_MARGIN] : 0,
    }),
);

export const SectionContainer = styled.div(({ $level = 0 }) =>
  css({
    width: '100%',
    mb: '70px',
    display: 'inline',

    '&': [
      '',
      '',
      '',
      {
        position: 'relative',
        display: 'block',
        paddingLeft: 'calc(185px + 24px)',
        paddingRight: 'calc(185px + 24px)',
        ml: $level ? `${$level * 12}px` : '0', // Only apply ml if level exists
      },
    ],

    '& a': {
      color: 'secondaryGreen',
      textDecoration: 'none',
      '@media print': {
        textDecoration: 'underline !important',
        color: '#1A3A35 !important',
      },

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  }),
);

export const SectionType = styled.h2(
  css({
    width: '162px',
    fontSize: pxToRem(22),
    lineHeight: pxToRem(24),
    letterSpacing: '0.05em',

    '&:first-letter': {
      textTransform: 'uppercase',
    },

    margin: ['2rem 0', '2rem 0', '2rem 0', 0],

    '&': [
      '',
      '',
      '',
      {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '185px',
      },
    ],
    '@media print': {
      width: '100%',
    },
  }),
);

// export const SectionText = styled(RawHtml)(
//   css({
//     lineHeight: pxToRem(24),

//     '& p': {
//       mb: '10px',

//       '& span': {
//         margin: '100px',
//         '&': {
//           fontFamily: 'Lato !important',
//         },
//       },
//     },

//     ...headerPack,
//   }),
// );

export const SectionContent = styled.div(({ $isIndented = false }) =>
  css({
    ml: $isIndented ? '12px' : 0,
    minHeight: ['', '', '25px'],
  }),
);

export const EventDate = styled.p(
  css({
    fontSize: '14px',
  }),
);

// News and Repors only

const EMPTY_SPACE_WIDTH = parseInt(CONTENT_TYPES_LEFT_MARGIN, 10) + 96 + 40;

export const InfoRow = styled(Flex)(
  css({
    mt: '24px',
    mb: ['70px', '50px', '30px'],
    gap: '28px',
    justifyContent: 'center',
    alignItems: 'flex-end',
  }),
);

export const Story = styled(LightRawHtml)(
  css({
    pl: [0, 0, CONTENT_TYPES_LEFT_MARGIN],
    width: ['100%', '100%', `calc((100% + ${EMPTY_SPACE_WIDTH}px) * 0.64)`],
  }),
);

export const Summary = styled(Story)(
  css({
    fontSize: pxToRem(22),
    textAlign: 'center',
    mb: '24px',
  }),
);

export const Sources = styled(Story)(
  css({
    '& p:first-of-type': {
      mt: '30px',
      mb: '12px',
    },
  }),
);

export const PressNavigation = styled(Flex)(
  css({
    flexDirection: ['column', 'column', 'row'],
    gap: ['30px', '30px', '70px'],
    mb: ['52px', '52px', '57px'],
  }),
  ({ $opacity, $isVisibleOnPrint }) =>
    css({
      opacity: $opacity,
      '@media print': $isVisibleOnPrint
        ? {
            opacity: 1,
          }
        : {},
    }),
);

// Sub-sections
export const SubSectionContainer = styled.div(
  css({
    mb: '32px',
  }),
);

export const SubSectionType = styled.h3(
  css({
    mb: '32px',
    fontSize: pxToRem(22),
    letterSpacing: '0.05em',

    '&:first-letter': {
      textTransform: 'uppercase',
    },
  }),
);

export const SubSectionText = styled(RawHtml)(
  css({
    width: '100%',
    lineHeight: pxToRem(24),
    display: 'inline',
    color: 'text',

    '& :is(ul, ol)': {
      mb: '10px',
    },

    '& p ': {
      mb: '10px',

      '& span': {
        '&': {
          lineHeight: '1.5rem !important',
          fontFamily: 'Lato !important',
        },
      },
    },

    '& pre': {
      color: '#1A3A35 !important',
      '& a': {
        color: '#32817D !important',
        textDecoration: 'none !important',
        '@media print': {
          textDecoration: 'underline !important',
          color: '#1A3A35 !important',
        },

        '&:hover': {
          textDecoration: 'underline !important',
        },
      },

      '& span': {
        fontSize: 'inherit !important',
        color: 'inherit !important',
        lineHeight: 'inherit !important',
        fontFamily: 'inherit !important',
      },
    },

    '& a': {
      color: '#32817D !important',
      textDecoration: 'none !important',
      '@media print': {
        textDecoration: 'underline !important',
        color: '#1A3A35 !important',
      },

      '&:hover': {
        textDecoration: 'underline !important',
      },

      '& span': {
        color: '#32817D !important',
        textDecoration: 'none !important',

        '&:hover': {
          textDecoration: 'underline !important',
        },
      },
    },

    '& span': {
      fontSize: 'inherit !important',
      color: 'text',
      lineHeight: '1.5rem !important',
      fontFamily: 'Lato !important',
    },

    img: {
      width: '50%',
      height: 'auto',
    },

    ...headerPack,
  }),
);

export const TOCWrapper = styled.div(
  css({
    float: ['', '', 'right'],
    width: ['', '', '330px'],
    padding: ['0 0 2rem', '0 0 2rem', '0 0 2rem 2rem'],
    marginRight: [0, 0, 0, 'calc(185px + 24px)'],
    zIndex: ['', '', 1],
    position: ['', '', 'relative'],
  }),
);

export const GalleryWrapper = styled.div(
  css({
    marginLeft: [0, 0, 0, 'calc(185px + 24px)'],
  }),
);

export const ArticleImage = styled.img(
  css({
    width: '100%',
    mb: '20px',
    objectFit: 'cover',
    '@media print': {
      width: '70%',
      height: 'auto',
      margin: '0 auto',
    },
  }),
);
